/**
 * AppointmentModal — Nueva cita / Editar cita
 * UX: Autocomplete con búsqueda en Contacto, Servicio, Profesional
 * Referencia: 0257 — NUEVA CITA, UX mejorada
 */
import React, { useState, useEffect, useCallback, useRef, useContext, useMemo } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Typography from "@material-ui/core/Typography";
import Autocomplete from "@material-ui/lab/Autocomplete";
import api from "../../services/api";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { STATUS_OPTIONS } from "./constants";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import { useDialogStyles } from "../../theme/redesign";
import { useStyles } from "./styles";
import ContactProfile360Modal from "../ContactProfile360Modal";
import Box from "@material-ui/core/Box";

const DEBOUNCE_MS = 300;
const MAX_CONTACT_RESULTS = 50;

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debouncedValue;
}

export default function AppointmentModal({
  open,
  onClose,
  appointment,
  onSaved,
  onStatusChange,
  ticketId = null,
  initialWhatsappId = null,
  initialContact = null
}) {
  const { user } = useContext(AuthContext);
  const dlg = useDialogStyles();
  const classes = useStyles();
  const isMountedRef = useRef(true);
  const [loading, setLoading] = useState(false);
  const [profile360Open, setProfile360Open] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [selectedContactObj, setSelectedContactObj] = useState(null);
  const [professionals, setProfessionals] = useState([]);
  const [services, setServices] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [form, setForm] = useState({
    contactId: "",
    whatsappId: "",
    professionalId: "",
    serviceId: "",
    appointmentDate: "",
    durationMin: 60,
    modality: "presencial",
    locationDetail: "",
    notes: ""
  });
  const [status, setStatus] = useState("");

  const debouncedContactSearch = useDebounce(contactSearch, DEBOUNCE_MS);

  const isEdit = !!appointment?.id;

  const formatContactLabel = useCallback((c) => {
    if (!c) return "";
    const name = (c.name || "").trim();
    const num = (c.number || "").trim();
    if (name && num) return `${name} (${num})`;
    if (name) return name;
    if (num) return num;
    return i18n.t("appointmentsModule.appointmentModal.contactFallback", { id: c.id });
  }, [i18n.language]);

  const statusMenuItems = useMemo(
    () =>
      STATUS_OPTIONS.map((o) => ({
        value: o.value,
        label: i18n.t(`appointmentsModule.status.${o.tKey}`)
      })),
    [i18n.language]
  );

  const fetchContacts = useCallback(async (searchName = "") => {
    try {
      setContactsLoading(true);
      const { data } = await api.get("/contacts/list", { params: searchName ? { name: searchName } : {} });
      if (!isMountedRef.current) return;
      const list = Array.isArray(data) ? data : [];
      setContacts(list.slice(0, MAX_CONTACT_RESULTS));
    } catch (e) {
      if (isMountedRef.current) setContacts([]);
    } finally {
      if (isMountedRef.current) setContactsLoading(false);
    }
  }, []);

  const toDateTimeLocal = (val) => {
    if (!val) return "";
    const s = String(val).trim().replace(" ", "T");
    return s.slice(0, 16);
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!open) return;
    if (appointment?.id) {
      setForm({
        contactId: appointment.contactId,
        whatsappId: "",
        professionalId: appointment.professionalId || "",
        serviceId: appointment.serviceId || "",
        appointmentDate: toDateTimeLocal(appointment.appointmentDate),
        durationMin: appointment.durationMin || 60,
        modality: appointment.modality || "presencial",
        locationDetail: appointment.locationDetail || "",
        notes: appointment.notes || ""
      });
      setStatus(appointment.status || "pending");
      setContactSearch("");
      setSelectedContactObj(null);
      return;
    }
    const cid = initialContact?.id ? initialContact.id : "";
    const wid = initialWhatsappId != null && initialWhatsappId !== "" ? initialWhatsappId : "";
    setForm({
      contactId: cid,
      whatsappId: wid,
      professionalId: "",
      serviceId: "",
      appointmentDate: "",
      durationMin: 60,
      modality: "presencial",
      locationDetail: "",
      notes: ""
    });
    setStatus("");
    if (initialContact?.id) {
      setSelectedContactObj(initialContact);
      setContactSearch(formatContactLabel(initialContact));
    } else {
      setSelectedContactObj(null);
      setContactSearch("");
    }
  }, [open, appointment?.id, initialContact?.id, initialWhatsappId, formatContactLabel]);

  useEffect(() => {
    if (!open || isEdit) return;
    (async () => {
      try {
        const { data } = await api.get("/whatsapp", { params: { companyId: user?.companyId } });
        if (!isMountedRef.current) return;
        setWhatsapps(Array.isArray(data) ? data : []);
      } catch {
        if (isMountedRef.current) setWhatsapps([]);
      }
    })();
  }, [open, isEdit, user?.companyId]);

  /** Si solo hay una conexión y aún no se eligió, preseleccionar (Agenda sin ticket). */
  useEffect(() => {
    if (!open || isEdit || whatsapps.length !== 1) return;
    setForm((f) => {
      if (f.whatsappId !== "" && f.whatsappId != null) return f;
      return { ...f, whatsappId: whatsapps[0].id };
    });
  }, [open, isEdit, whatsapps]);

  useEffect(() => {
    if (!open) return;
    if (debouncedContactSearch.length < 2) {
      setContacts(selectedContactObj ? [selectedContactObj] : []);
      return;
    }
    fetchContacts(debouncedContactSearch);
  }, [open, debouncedContactSearch, fetchContacts, selectedContactObj]);

  useEffect(() => {
    if (!open) return;
    const loadConfig = async () => {
      try {
        const { data } = await api.get("/onboarding/config");
        setProfessionals(data?.professionals || []);
        setServices(data?.services || []);
      } catch (e) {
        setProfessionals([]);
        setServices([]);
      }
    };
    loadConfig();
  }, [open]);

  const safeSetState = (fn) => {
    if (isMountedRef.current) fn();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.contactId || !form.appointmentDate) {
      toast.error(i18n.t("appointmentsModule.appointmentModal.toastContactDateRequired"));
      return;
    }
    if (!form.whatsappId) {
      toast.error(i18n.t("appointmentsModule.appointmentModal.toastWhatsappRequired"));
      return;
    }
    try {
      setLoading(true);
      if (isEdit) {
        if (onStatusChange) await onStatusChange(appointment.id, status);
        safeSetState(() => toast.success(i18n.t("appointmentsModule.appointmentModal.toastStatusUpdated")));
      } else {
        await api.post("/appointments", {
          contactId: +form.contactId,
          ticketId: ticketId || null,
          whatsappId: +form.whatsappId,
          professionalId: form.professionalId ? +form.professionalId : null,
          serviceId: form.serviceId ? +form.serviceId : null,
          appointmentDate: form.appointmentDate.replace("T", " ") + ":00",
          durationMin: +form.durationMin || 60,
          modality: form.modality || "presencial",
          locationDetail: form.locationDetail || null,
          notes: form.notes || null
        });
        safeSetState(() => toast.success(i18n.t("appointmentsModule.appointmentModal.toastCreated")));
      }
      safeSetState(() => { onSaved?.(); onClose?.(); });
    } catch (e) { toastError(e); } finally { safeSetState(() => setLoading(false)); }
  };

  const handleStatusChange = async () => {
    if (!isEdit || !onStatusChange) return;
    try {
      setLoading(true);
      await onStatusChange(appointment.id, status);
      if (isMountedRef.current) {
        toast.success(i18n.t("appointmentsModule.appointmentModal.toastStatusUpdated"));
        onSaved?.();
        onClose?.();
      }
    } catch (e) { toastError(e); } finally { if (isMountedRef.current) setLoading(false); }
  };

  const handleReschedule = async () => {
    if (!isEdit || !form.appointmentDate) {
      toast.error(i18n.t("appointmentsModule.appointmentModal.toastRescheduleDateRequired"));
      return;
    }
    try {
      setLoading(true);
      const appointmentDate = form.appointmentDate.includes("T")
        ? form.appointmentDate.replace("T", " ") + ":00"
        : form.appointmentDate;
      await api.patch(`/appointments/${appointment.id}`, {
        appointmentDate,
        durationMin: form.durationMin ? +form.durationMin : undefined,
        professionalId: form.professionalId ? +form.professionalId : undefined,
        serviceId: form.serviceId ? +form.serviceId : undefined,
        modality: form.modality || undefined,
        locationDetail: form.locationDetail || undefined,
        notes: form.notes || undefined
      });
      if (isMountedRef.current) {
        toast.success(i18n.t("appointmentsModule.appointmentModal.toastRescheduled"));
        onSaved?.();
        onClose?.();
      }
    } catch (e) { toastError(e); } finally { if (isMountedRef.current) setLoading(false); }
  };

  const contactOptions = selectedContactObj && !contacts.some((c) => c.id === selectedContactObj.id)
    ? [selectedContactObj, ...contacts]
    : contacts;
  const selectedContact = selectedContactObj || contacts.find((c) => c.id === form.contactId) || null;
  const selectedService = services.find((s) => s.id === form.serviceId) || null;
  const selectedProfessional = professionals.find((p) => p.id === form.professionalId) || null;

  const profile360ContactId =
    isEdit && appointment?.contactId
      ? +appointment.contactId
      : form.contactId
        ? +form.contactId
        : null;

  const dash = i18n.t("appointmentsModule.appointmentModal.dash");
  const formatDateTime = (value) => value ? new Date(value).toLocaleString() : dash;
  const displayValue = (value) => {
    if (value === true) return i18n.t("appointmentsModule.appointmentModal.yes");
    if (value === false) return i18n.t("appointmentsModule.appointmentModal.no");
    if (value === 0) return "0";
    return value || dash;
  };

  const renderDetailItem = (label, value) => (
    <Box>
      <Typography variant="caption" color="textSecondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" style={{ wordBreak: "break-word" }}>
        {displayValue(value)}
      </Typography>
    </Box>
  );

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      classes={{ paper: `${dlg.paper} ${classes.dialogPaper}` }}
    >
      <DialogTitle>
        {isEdit
          ? i18n.t("appointmentsModule.appointmentModal.titleEdit", { code: appointment?.confirmationCode || "" })
          : i18n.t("appointmentsModule.appointmentModal.titleNew")}
      </DialogTitle>
      <form onSubmit={handleSubmit} className={classes.form}>
        <DialogContent dividers className={classes.dialogContent}>
          {isEdit ? (
            <>
              <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 12 }}>
                {i18n.t("appointmentsModule.appointmentModal.detailsTitle")}
              </Typography>
              <Box
                display="grid"
                gridTemplateColumns="repeat(2, minmax(0, 1fr))"
                gridGap={12}
                mb={2}
              >
                {renderDetailItem(i18n.t("appointmentsModule.appointmentModal.confirmationCode"), appointment?.confirmationCode)}
                {renderDetailItem(i18n.t("appointmentsModule.appointmentModal.client"), appointment?.contactName)}
                {renderDetailItem(i18n.t("appointmentsModule.appointmentModal.service"), appointment?.serviceName)}
                {renderDetailItem(i18n.t("appointmentsModule.appointmentModal.professional"), appointment?.professionalName)}
                {renderDetailItem(i18n.t("appointmentsModule.appointmentModal.date"), formatDateTime(appointment?.appointmentDate))}
                {renderDetailItem(i18n.t("appointmentsModule.appointmentModal.duration"), appointment?.durationMin)}
                {renderDetailItem(i18n.t("appointmentsModule.appointmentModal.modality"), appointment?.modality)}
                {renderDetailItem(i18n.t("appointmentsModule.appointmentModal.locationDetail"), appointment?.locationDetail)}
                {renderDetailItem(i18n.t("appointmentsModule.appointmentModal.bookedByAi"), appointment?.bookedByAi)}
                {renderDetailItem(i18n.t("appointmentsModule.appointmentModal.createdAt"), formatDateTime(appointment?.createdAt))}
                {renderDetailItem(i18n.t("appointmentsModule.appointmentModal.updatedAt"), formatDateTime(appointment?.updatedAt))}
              </Box>
              {appointment?.notes ? (
                <Box mb={2}>
                  {renderDetailItem(i18n.t("appointmentsModule.appointmentModal.notes"), appointment.notes)}
                </Box>
              ) : null}
              <FormControl fullWidth style={{ marginTop: 16 }}>
                <InputLabel>{i18n.t("appointmentsModule.appointmentModal.statusLabel")}</InputLabel>
                <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                  {statusMenuItems.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {appointment?.contactId ? (
                <Box mt={1} mb={1}>
                  <Button size="small" variant="outlined" color="primary" type="button" onClick={() => setProfile360Open(true)}>
                    {i18n.t("appointmentsModule.appointmentModal.viewProfile360")}
                  </Button>
                </Box>
              ) : null}
              {status === "rescheduled" && (
                <div style={{ marginTop: 16 }}>
                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                    {i18n.t("appointmentsModule.appointmentModal.rescheduleHint")}
                  </Typography>
                  <Autocomplete
                    value={selectedService}
                    onChange={(_, v) => setForm((f) => ({ ...f, serviceId: v ? v.id : "" }))}
                    options={services}
                    getOptionLabel={(s) => (s && s.serviceName) || ""}
                    getOptionSelected={(opt, val) => opt?.id === val?.id}
                    renderInput={(params) => (
                      <TextField {...params} label={i18n.t("appointmentsModule.appointmentModal.serviceField")} margin="normal" variant="outlined" />
                    )}
                    style={{ marginBottom: 8 }}
                  />
                  <Autocomplete
                    value={selectedProfessional}
                    onChange={(_, v) => setForm((f) => ({ ...f, professionalId: v ? v.id : "" }))}
                    options={professionals}
                    getOptionLabel={(p) => (p && p.name) || ""}
                    getOptionSelected={(opt, val) => opt?.id === val?.id}
                    renderInput={(params) => (
                      <TextField {...params} label={i18n.t("appointmentsModule.appointmentModal.professionalField")} margin="normal" variant="outlined" />
                    )}
                    style={{ marginBottom: 8 }}
                  />
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label={i18n.t("appointmentsModule.appointmentModal.newDateTime")}
                    value={form.appointmentDate}
                    onChange={(e) => setForm((f) => ({ ...f, appointmentDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    required
                    margin="normal"
                    variant="outlined"
                    style={{ marginBottom: 8 }}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label={i18n.t("appointmentsModule.appointmentModal.duration")}
                    value={form.durationMin}
                    onChange={(e) => setForm((f) => ({ ...f, durationMin: +e.target.value || 60 }))}
                    margin="normal"
                    variant="outlined"
                    style={{ marginBottom: 8 }}
                  />
                  <FormControl variant="outlined" fullWidth style={{ marginTop: 8, marginBottom: 8 }}>
                    <InputLabel>{i18n.t("appointmentsModule.appointmentModal.modality")}</InputLabel>
                    <Select
                      value={form.modality}
                      onChange={(e) => setForm((f) => ({ ...f, modality: e.target.value }))}
                      label={i18n.t("appointmentsModule.appointmentModal.modality")}
                    >
                      <MenuItem value="presencial">{i18n.t("appointmentsModule.appointmentModal.modalityPresential")}</MenuItem>
                      <MenuItem value="online">{i18n.t("appointmentsModule.appointmentModal.modalityOnline")}</MenuItem>
                      <MenuItem value="phone">{i18n.t("appointmentsModule.appointmentModal.modalityPhone")}</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    label={i18n.t("appointmentsModule.appointmentModal.locationDetail")}
                    value={form.locationDetail}
                    onChange={(e) => setForm((f) => ({ ...f, locationDetail: e.target.value }))}
                    margin="normal"
                    variant="outlined"
                    style={{ marginBottom: 8 }}
                  />
                  <TextField
                    fullWidth
                    label={i18n.t("appointmentsModule.appointmentModal.notes")}
                    value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    multiline
                    rows={2}
                    margin="normal"
                    variant="outlined"
                  />
                </div>
              )}
            </>
          ) : (
            <>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                {i18n.t("appointmentsModule.appointmentModal.introNew")}
              </Typography>
              <Autocomplete
                value={selectedContact}
                onChange={(_, v) => {
                  setSelectedContactObj(v);
                  setForm((f) => ({ ...f, contactId: v ? v.id : "" }));
                }}
                inputValue={contactSearch}
                onInputChange={(_, v) => setContactSearch(v)}
                options={contactOptions}
                getOptionLabel={formatContactLabel}
                getOptionSelected={(opt, val) => opt?.id === val?.id}
                loading={contactsLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={i18n.t("appointmentsModule.appointmentModal.contactLabel")}
                    required={!form.contactId}
                    placeholder={i18n.t("appointmentsModule.appointmentModal.contactPlaceholder")}
                    helperText={i18n.t("appointmentsModule.appointmentModal.contactHelper")}
                    margin="normal"
                    variant="outlined"
                  />
                )}
                noOptionsText={contactSearch.length < 2 ? i18n.t("appointmentsModule.appointmentModal.noOptionsShort") : i18n.t("appointmentsModule.appointmentModal.noOptionsEmpty")}
                style={{ marginBottom: 8 }}
              />
              {form.contactId ? (
                <Box mb={2}>
                  <Button size="small" variant="outlined" color="primary" type="button" onClick={() => setProfile360Open(true)}>
                    {i18n.t("appointmentsModule.appointmentModal.viewProfile360")}
                  </Button>
                </Box>
              ) : null}
              <FormControl variant="outlined" fullWidth style={{ marginBottom: 16 }} required>
                <InputLabel id="appointment-whatsapp-label" shrink>
                  {i18n.t("appointmentsModule.appointmentModal.connectionLabel")}
                </InputLabel>
                <Select
                  labelId="appointment-whatsapp-label"
                  label={i18n.t("appointmentsModule.appointmentModal.connectionLabel")}
                  displayEmpty
                  notched
                  value={form.whatsappId === "" || form.whatsappId == null ? "" : form.whatsappId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((f) => ({ ...f, whatsappId: v === "" ? "" : Number(v) }));
                  }}
                  renderValue={(selected) => {
                    if (selected === "" || selected == null) {
                      return (
                        <span style={{ color: "#999" }}>
                          {i18n.t("appointmentsModule.appointmentModal.connectionPlaceholder")}
                        </span>
                      );
                    }
                    const w = whatsapps.find((x) => Number(x.id) === Number(selected));
                    if (!w) return i18n.t("appointmentsModule.appointmentModal.dash");
                    return (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <span style={{ marginRight: 8 }} role="img" aria-label="WhatsApp">
                          📱
                        </span>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontWeight: "bold", fontSize: 14 }}>
                              {w.name || i18n.t("appointmentsModule.appointmentModal.connectionNamed", { id: w.id })}
                            </span>
                            <span
                              style={{
                                fontSize: 10,
                                padding: "2px 6px",
                                borderRadius: 10,
                                backgroundColor: w.status === "CONNECTED" ? "#25d366" : "#e1306c",
                                color: "white",
                                fontWeight: "bold"
                              }}
                            >
                              {w.status || "DISCONNECTED"}
                            </span>
                          </div>
                          {w.waName ? (
                            <span style={{ fontSize: 12, color: "#666" }}>{w.waName}</span>
                          ) : null}
                        </div>
                      </div>
                    );
                  }}
                >
                  {whatsapps.map((w) => (
                    <MenuItem key={w.id} value={w.id}>
                      <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                        <span style={{ marginRight: 8 }} role="img" aria-label="WhatsApp">
                          📱
                        </span>
                        <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontWeight: "bold", fontSize: 14 }}>
                              {w.name || i18n.t("appointmentsModule.appointmentModal.connectionNamed", { id: w.id })}
                            </span>
                            <span
                              style={{
                                fontSize: 10,
                                padding: "2px 6px",
                                borderRadius: 10,
                                backgroundColor: w.status === "CONNECTED" ? "#25d366" : "#e1306c",
                                color: "white",
                                fontWeight: "bold"
                              }}
                            >
                              {w.status || "DISCONNECTED"}
                            </span>
                          </div>
                          {w.waName ? (
                            <span style={{ fontSize: 12, color: "#666" }}>{w.waName}</span>
                          ) : null}
                        </div>
                      </div>
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 6, display: "block" }}>
                  {i18n.t("appointmentsModule.appointmentModal.connectionHelper")}
                </Typography>
              </FormControl>
              <Autocomplete
                value={selectedService}
                onChange={(_, v) => setForm((f) => ({ ...f, serviceId: v ? v.id : "" }))}
                options={services}
                getOptionLabel={(s) => (s && s.serviceName) || ""}
                getOptionSelected={(opt, val) => opt?.id === val?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={i18n.t("appointmentsModule.appointmentModal.serviceField")}
                    placeholder={i18n.t("appointmentsModule.appointmentModal.serviceSearchPh")}
                    helperText={i18n.t("appointmentsModule.appointmentModal.serviceHelper")}
                    margin="normal"
                    variant="outlined"
                  />
                )}
                noOptionsText={i18n.t("appointmentsModule.appointmentModal.noServices")}
                style={{ marginBottom: 8 }}
              />
              <Autocomplete
                value={selectedProfessional}
                onChange={(_, v) => setForm((f) => ({ ...f, professionalId: v ? v.id : "" }))}
                options={professionals}
                getOptionLabel={(p) => (p && p.name) || ""}
                getOptionSelected={(opt, val) => opt?.id === val?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={i18n.t("appointmentsModule.appointmentModal.professionalField")}
                    placeholder={i18n.t("appointmentsModule.appointmentModal.professionalSearchPh")}
                    helperText={i18n.t("appointmentsModule.appointmentModal.professionalHelper")}
                    margin="normal"
                    variant="outlined"
                  />
                )}
                noOptionsText={i18n.t("appointmentsModule.appointmentModal.noProfessionalsConfigured")}
                style={{ marginBottom: 8 }}
              />
              <TextField
                fullWidth
                type="datetime-local"
                label={i18n.t("appointmentsModule.appointmentModal.datetimeLabel")}
                value={form.appointmentDate}
                onChange={(e) => setForm((f) => ({ ...f, appointmentDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                required
                helperText={i18n.t("appointmentsModule.appointmentModal.datetimeHelper")}
                margin="normal"
                variant="outlined"
                style={{ marginTop: 8, marginBottom: 8 }}
              />
              <TextField
                fullWidth
                type="number"
                label={i18n.t("appointmentsModule.appointmentModal.duration")}
                value={form.durationMin}
                onChange={(e) => setForm((f) => ({ ...f, durationMin: +e.target.value || 60 }))}
                helperText={i18n.t("appointmentsModule.appointmentModal.durationHelper")}
                margin="normal"
                variant="outlined"
                style={{ marginBottom: 8 }}
              />
              <FormControl variant="outlined" fullWidth style={{ marginTop: 8, marginBottom: 8 }}>
                <InputLabel>{i18n.t("appointmentsModule.appointmentModal.modality")}</InputLabel>
                <Select
                  value={form.modality}
                  onChange={(e) => setForm((f) => ({ ...f, modality: e.target.value }))}
                  label={i18n.t("appointmentsModule.appointmentModal.modality")}
                >
                  <MenuItem value="presencial">{i18n.t("appointmentsModule.appointmentModal.modalityPresential")}</MenuItem>
                  <MenuItem value="online">{i18n.t("appointmentsModule.appointmentModal.modalityOnline")}</MenuItem>
                  <MenuItem value="phone">{i18n.t("appointmentsModule.appointmentModal.modalityPhone")}</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label={i18n.t("appointmentsModule.appointmentModal.locationDetail")}
                value={form.locationDetail}
                onChange={(e) => setForm((f) => ({ ...f, locationDetail: e.target.value }))}
                placeholder={i18n.t("appointmentsModule.appointmentModal.locationPlaceholder")}
                margin="normal"
                variant="outlined"
                style={{ marginBottom: 8 }}
              />
              <TextField
                fullWidth
                label={i18n.t("appointmentsModule.appointmentModal.notes")}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                multiline
                rows={2}
                placeholder={i18n.t("appointmentsModule.appointmentModal.notesPlaceholder")}
                margin="normal"
                variant="outlined"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{i18n.t("appointmentsModule.appointmentModal.cancel")}</Button>
          {isEdit ? (
            status === "rescheduled" ? (
              <Button variant="contained" color="primary" onClick={handleReschedule} disabled={loading || !form.appointmentDate}>{i18n.t("appointmentsModule.appointmentModal.saveReschedule")}</Button>
            ) : (
              <Button variant="contained" color="primary" onClick={handleStatusChange} disabled={loading}>{i18n.t("appointmentsModule.appointmentModal.saveStatus")}</Button>
            )
          ) : (
            <Button type="submit" variant="contained" color="primary" disabled={loading}>{i18n.t("appointmentsModule.appointmentModal.create")}</Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
    <ContactProfile360Modal
      open={profile360Open}
      onClose={() => setProfile360Open(false)}
      contactId={profile360ContactId}
    />
  </>
  );
}
